import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ChartConfiguration, ChartData, ChartType } from "chart.js/auto";
import { formatCurrency, formatNumber } from "@angular/common";
import { Currency, DEFAULT_CURRENCY, getCurrency } from "../../common/organisation";
import { OrganisationService } from "../../organisation/organisation.service";
import { abbreviate } from "../bar-chart/bar-chart.component";

// the chart's text and lines, in the app's palette
const INK = '#1E2233'
const SLATE = '#5b6172'
const GRID = '#eef0f4'
const FONT_FAMILY = "'Montserrat', 'Roboto', system-ui, sans-serif"
// the largest bubble's radius, in pixels; the smallest is kept visible
const MAX_RADIUS = 26
const MIN_RADIUS = 3
// a bubble this big or bigger carries its name
const LABEL_RADIUS = 9

export class BubbleData {
    constructor(public _key:String, public name:string, public x:number, public y: number, public z:number) {}
}

export class BubbleDataSet {
    constructor(public name:string, public colour:string, public hover:string, public data:BubbleData[]) {}
}

// a colour made see-through, so overlapping bubbles still show
const translucent = (colour:string, alpha:number) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(colour || '')
    if (!m) return colour
    const [r, g, b] = m.slice(1).map(h => parseInt(h, 16))
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

@Component({
    selector: 'app-bubble-chart',
    templateUrl: './bubble-chart.html',
    standalone: false,
    styleUrls: ['./bubble-chart.css']
})
export class BubbleChartComponent implements OnInit, OnChanges {
    @Input() nodeData:BubbleDataSet[]
    @Input() axislabels:string[]
    @Input() maxValue:number
    @Input() size = ['700px', '375px']                      // width, height

    @Output() selectNodeEvent = new EventEmitter<string>();

    public bubbleChartOptions: ChartConfiguration['options'] = {}
    public bubbleChartType: ChartType = 'bubble';
    public bubbleChartLegend = true;
    public bubblePlugins = [ChartDataLabels]

    currency:Currency

    nodeLabels = []
    values = []
    nodeKeys:String[][] = []                                 // by dataset, then bubble
    public bubbleChartData: ChartData<'bubble'>

    style:string = "position: relative; height:375px; width:700px;"

    constructor(@Inject(LOCALE_ID) private locale: string, organisationService:OrganisationService) {
        this.currency = getCurrency(organisationService.organisation?.currencyCode||DEFAULT_CURRENCY)
    }

    ngOnChanges(changes: SimpleChanges): void {
        // the axes are named again with the data, as the attributes charted can change
        if (changes["nodeData"] || changes["axislabels"]) {
            this.configure()
            this.populateData()
        }
    }

    ngOnInit(): void {
        this.configure()
    }

    // a value as its axis shows it: money as money
    private format(label:string, value:number, short:boolean) {
        if (value === null || value === undefined || isNaN(value)) return ''
        if (label === 'Cost' || label === 'Revenue') {
            return short ? this.currency.symbol + abbreviate(value, this.locale) : formatCurrency(value, this.locale, this.currency.symbol, this.currency.code, '1.0-0')
        }
        return short ? abbreviate(value, this.locale) : formatNumber(value, this.locale, '1.0-2')
    }

    configure() {
        const labels = this.axislabels || []
        const axis = (i:number) => ({
            title: { display: !!labels[i], text: labels[i], color: SLATE, font: { family: FONT_FAMILY, size: 12, weight: 600 } },
            beginAtZero: true,
            grace: '8%',
            grid: { color: GRID, drawTicks: false },
            border: { display: false },
            ticks: { color: SLATE, padding: 6, font: { family: FONT_FAMILY, size: 11 }, callback: (v:any) => this.format(labels[i], Number(v), true) },
        })
        this.bubbleChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 12, right: 24 } },
            scales: { x: axis(0) as any, y: axis(1) as any },
            plugins: {
                tooltip: {
                    backgroundColor: INK,
                    padding: 10,
                    cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 },
                    bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: {
                        title: (items:any[]) => items.map((i:any) => i.raw.label).join(", "),
                        // one line a measure: each named once
                        label: (context:any) => {
                            const z = this.nodeData[context.datasetIndex]?.data[context.dataIndex]?.z
                            return [
                                `${labels[0]}: ${this.format(labels[0], context.parsed.x, false)}`,
                                `${labels[1]}: ${this.format(labels[1], context.parsed.y, false)}`,
                                `${labels[2]}: ${this.format(labels[2], z, false)}`,
                            ]
                        },
                    },
                },
                // a name on the bubbles big enough to hold one; the rest are named on hover
                datalabels: {
                    clip: false,
                    display: (context:any) => (context.dataset.data[context.dataIndex]?.r || 0) >= LABEL_RADIUS ? 'auto' : false,
                    anchor: 'center',
                    align: 'center',
                    color: INK,
                    formatter: (value:any, context:any) => {
                        const name:string = context.dataset.data[context.dataIndex].label || ''
                        return name.length > 18 ? name.substring(0, 17) + '…' : name
                    },
                    font: { family: FONT_FAMILY, size: 10, weight: 500 },
                },
                legend: {
                    // one set of bubbles needs no key: the title says what they are
                    display: (this.nodeData?.length || 0) > 1,
                    position: 'top',
                    align: 'end',
                    labels: { color: SLATE, usePointStyle: true, boxWidth: 10, boxHeight: 10, font: { family: FONT_FAMILY, size: 12 } },
                }
            }
        } as any
    }

    populateData() {
        this.style = `position: relative; height:${this.size[1]}; width:${this.size[0]};`
        this.nodeLabels = []

        setTimeout(() => {
            this.nodeKeys = []
            const datasets = (this.nodeData || []).filter(nd => nd).map(nd => {
                const keys:String[] = []
                const data = nd.data.map(d => {
                    // the bubble's area, not its radius, grows with the value, so twice the value looks twice as big
                    const radius = this.maxValue > 0 ? MAX_RADIUS * Math.sqrt(Math.max(0, d.z) / this.maxValue) : MAX_RADIUS / 2
                    keys.push(d._key)
                    return { x: d.x || 0, y: d.y || 0, r: Math.max(MIN_RADIUS, radius), label: d.name }
                })
                this.nodeKeys.push(keys)
                return {
                    data, label: nd.name,
                    backgroundColor: translucent(nd.colour, 0.55), hoverBackgroundColor: translucent(nd.hover || nd.colour, 0.85),
                    borderColor: nd.colour, borderWidth: 1.5, hoverBorderWidth: 2,
                }
            })
            this.bubbleChartData = {labels:this.axislabels, datasets}
        })
    }

    // the node behind the bubble clicked (the first bubble included)
    onSelect(data): void {
        const active = data?.active?.[0]
        if (!active) return
        const key = this.nodeKeys[active.datasetIndex]?.[active.index]
        if (key) this.selectNodeEvent.emit(key as string)
    }
}
